"use client"

import { useState } from "react"
import { Edit, Calendar, MapPin, Share2, CalendarPlus, Ticket } from "lucide-react"
import config from "../config"
import { formatEventDateTime, formatMoney, downloadEventICS } from "../utils/format"

const EventHeader = ({ event, isCreator, isLoggedIn, navigate, event_slug }) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const when = formatEventDateTime(event.date, event.time, event.timezone)
  const currency = event.currency || "KES"

  // Determine a price label from tiers (if any) or the flat ticket price.
  const tiers = (event.ticket_tiers || []).filter((t) => t.is_active !== false)
  let priceLabel = "Free"
  if (tiers.length > 0) {
    const prices = tiers.map((t) => Number(t.price || 0))
    const min = Math.min(...prices)
    priceLabel = min > 0 ? `From ${formatMoney(min, currency)}` : "Free"
  } else if (event.is_paid_event && Number(event.ticket_price) > 0) {
    priceLabel = formatMoney(event.ticket_price, currency)
  }

  const handleShare = async () => {
    const url = window.location.href
    const shareData = { title: event.title, text: event.description?.slice(0, 140), url }
    try {
      if (navigator.share) {
        await navigator.share(shareData)
      } else {
        await navigator.clipboard.writeText(url)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch (e) { /* user dismissed */ }
  }

  return (
    <>
      {/* Hero Image Section */}
      <div className="relative overflow-hidden rounded-2xl shadow-2xl mb-8">
        <div className="relative cursor-pointer group" onClick={() => setIsImageModalOpen(true)}>
          <img
            src={`${config.MEDIA_BASE_URL}${event.thumbnail_photo}`}
            alt={event.title}
            className="w-full h-96 object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

          {/* Category + price badges */}
          <div className="absolute top-6 right-6 flex flex-col items-end gap-2">
            <div className="bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg font-medium capitalize">
              {event.category_display || event.category}
            </div>
            <div className="bg-white/95 text-gray-900 px-4 py-2 rounded-full shadow-lg font-semibold flex items-center gap-1">
              <Ticket size={15} /> {priceLabel}
            </div>
          </div>

          {/* Edit Button for Creator */}
          {isLoggedIn && isCreator && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/events/${event_slug}/update`) }}
              className="absolute top-6 left-6 bg-white/90 hover:bg-white text-gray-800 px-4 py-2 rounded-full shadow-lg font-medium transition-all duration-200 flex items-center gap-2"
            >
              <Edit size={16} /> Edit Event
            </button>
          )}

          {/* Event Title Overlay */}
          <div className="absolute bottom-6 left-6 right-6">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 drop-shadow-lg">{event.title}</h1>
            <div className="flex flex-wrap gap-4 text-white/90">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span className="font-medium">{when.full}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={18} />
                <span className="font-medium">{event.location}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Share / Calendar actions */}
      <div className="flex flex-wrap gap-3 mb-8">
        <button
          onClick={handleShare}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
        >
          <Share2 size={16} /> {copied ? "Link copied!" : "Share"}
        </button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(`${event.title} — ${typeof window !== "undefined" ? window.location.href : ""}`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.582 0 11.941-5.359 11.944-11.893a11.821 11.821 0 00-3.487-8.401" />
          </svg>
          WhatsApp
        </a>
        <button
          onClick={() => downloadEventICS(event)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
        >
          <CalendarPlus size={16} /> Add to Calendar
        </button>
        {event.location_url && (
          <a
            href={event.location_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
          >
            <MapPin size={16} /> View Map
          </a>
        )}
      </div>

      {/* Image Modal */}
      {isImageModalOpen && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setIsImageModalOpen(false)}>
          <div className="relative max-w-6xl max-h-full">
            <img
              src={`${config.MEDIA_BASE_URL}${event.thumbnail_photo}`}
              alt={event.title}
              className="max-w-full max-h-full rounded-lg shadow-2xl"
            />
            <button
              className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full text-xl transition-colors duration-200"
              onClick={() => setIsImageModalOpen(false)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default EventHeader
